/**
 * Server-rendered JSON-LD for the glossary hub (DefinedTermSet + BreadcrumbList).
 *
 * Emitted from each glossary route's `head()` so crawlers and AI answer engines
 * see the definitions in the initial HTML, without executing JavaScript.
 */
import { SITE_URL } from "@/config/site";
import { GLOSSARY_TERMS, glossaryText } from "@/data/glossary";
import type { SiteLang } from "@/hooks/useLanguage";

const CHROME: Record<SiteLang, { name: string; description: string; hub: string; hubPath: string; home: string }> = {
  en: {
    name: "Life Sciences Regulatory Glossary",
    description:
      "Definitions of the regulations, standards, and acronyms used across pharma, biotech, medical devices, and IVD.",
    hub: "Knowledge Center",
    hubPath: "/en/knowledge-center",
    home: "Home",
  },
  es: {
    name: "Glosario Regulatorio de Life Sciences",
    description:
      "Definiciones de los reglamentos, normas y siglas utilizados en pharma, biotech, Medical Devices e IVD.",
    hub: "Recursos",
    hubPath: "/es/recursos",
    home: "Inicio",
  },
  "zh-cn": {
    name: "生命科学法规术语表",
    description: "解释制药、生物技术、医疗器械和 IVD 领域使用的法规、标准与缩写。",
    hub: "知识中心",
    hubPath: "/zh-cn/knowledge-center",
    home: "首页",
  },
};

const HOME_PATH: Record<SiteLang, string> = {
  en: "/en",
  es: "/es",
  "zh-cn": "/zh-cn",
};

export function buildGlossaryJsonLd(lang: SiteLang, path: string) {
  const chrome = CHROME[lang] ?? CHROME.en;
  const url = `${SITE_URL}${path}`;

  const definedTermSet = {
    "@context": "https://schema.org",
    "@type": "DefinedTermSet",
    "@id": url,
    name: chrome.name,
    description: chrome.description,
    url,
    inLanguage: lang === "zh-cn" ? "zh-Hans" : lang,
    publisher: { "@type": "Organization", name: "QbD Group", url: SITE_URL },
    hasDefinedTerm: GLOSSARY_TERMS.map((entry) => ({
      "@type": "DefinedTerm",
      "@id": `${url}#${entry.slug}`,
      name: entry.term,
      ...(entry.expansion ? { alternateName: glossaryText(entry.expansion, lang) } : {}),
      description: glossaryText(entry.definition, lang),
      inDefinedTermSet: url,
    })),
  };

  const breadcrumbs = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: chrome.home, item: `${SITE_URL}${HOME_PATH[lang] ?? "/en"}` },
      { "@type": "ListItem", position: 2, name: chrome.hub, item: `${SITE_URL}${chrome.hubPath}` },
      { "@type": "ListItem", position: 3, name: chrome.name },
    ],
  };

  return [
    { type: "application/ld+json", children: JSON.stringify(definedTermSet) },
    { type: "application/ld+json", children: JSON.stringify(breadcrumbs) },
  ];
}

/**
 * Per-term page JSON-LD (DefinedTerm + BreadcrumbList) plus its title/description.
 */
export function buildGlossaryTermSeo(lang: SiteLang, hubPath: string, slug: string) {
  const chrome = CHROME[lang] ?? CHROME.en;
  const entry = GLOSSARY_TERMS.find((e) => e.slug === slug);
  const url = `${SITE_URL}${hubPath}/${slug}`;

  if (!entry) {
    return {
      title: `${chrome.name} | QbD Group`,
      description: chrome.description,
      noIndex: true,
      scripts: [] as { type: string; children: string }[],
    };
  }

  const definition = glossaryText(entry.definition, lang);
  const expansion = entry.expansion ? glossaryText(entry.expansion, lang) : undefined;
  const suffix: Record<SiteLang, string> = {
    en: "Definition",
    es: "Definición",
    "zh-cn": "定义",
  };

  const definedTerm = {
    "@context": "https://schema.org",
    "@type": "DefinedTerm",
    "@id": url,
    name: entry.term,
    ...(expansion ? { alternateName: expansion } : {}),
    description: definition,
    url,
    inLanguage: lang === "zh-cn" ? "zh-Hans" : lang,
    inDefinedTermSet: {
      "@type": "DefinedTermSet",
      "@id": `${SITE_URL}${hubPath}`,
      name: chrome.name,
      url: `${SITE_URL}${hubPath}`,
    },
    publisher: { "@type": "Organization", name: "QbD Group", url: SITE_URL },
  };

  const breadcrumbs = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: chrome.home, item: `${SITE_URL}${HOME_PATH[lang] ?? "/en"}` },
      { "@type": "ListItem", position: 2, name: chrome.hub, item: `${SITE_URL}${chrome.hubPath}` },
      { "@type": "ListItem", position: 3, name: chrome.name, item: `${SITE_URL}${hubPath}` },
      { "@type": "ListItem", position: 4, name: entry.term },
    ],
  };

  return {
    title: `${entry.term} — ${expansion ?? suffix[lang] ?? suffix.en} | QbD Group`,
    description: definition.length > 155 ? `${definition.slice(0, 152).trimEnd()}...` : definition,
    noIndex: false,
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(definedTerm) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbs) },
    ],
  };
}
